// Libraries
import React, { useEffect, createElement, useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import get from 'lodash/get';
// Hooks and utils
import useAPI from 'src/hooks/useAPI';
import useNotice from 'src/hooks/useNotice';
import useEventListener from 'src/hooks/useEventListener';
import { replace, skuPath, productPath, push } from 'src/utils/paths';
import {
  trimSeoName,
  getInstallationPrice,
  getMemberInstallationPrice,
} from 'src/containers/ProductPage/utils';
import {
  getSubscriptionPlansInfo,
  formatPlansForReducer,
  getUpsellPlanIds,
} from 'src/components/Plans/Pricing/utils';
import { logger } from 'src/utils/logger';
// Components
import { Grid } from 'src/components/HTKit/Grid';
import PageLoader from 'src/components/PageLoader';
import ProductHeaderBlock from 'src/components/Product/ProductHeader';
import ProductFeatures from 'src/components/Product/ProductFeatures';
import ProductWorksWith from 'src/components/Product/ProductWorksWith';
import ProductInstallation from 'src/components/Product/ProductInstallation';
import ProductSpecifications from 'src/components/Product/ProductSpecifications';
import ProductFaq from 'src/components/Product/ProductFaq';
import ProductCompatibility from 'src/components/Product/ProductCompatibility';
import ProductHero from 'src/components/Product/ProductHero';
import ServiceMeta from 'src/components/Service/ServiceMeta';
import Breadcrumbs from 'src/components/Elements/Breadcrumbs';
import CustomerReviews from 'src/components/Elements/CustomerReviews';
import ProductSpecialistModal from 'src/components/Product/ProductHeader/ProductSpecialistModal';
import FieldSaleGuard from 'src/containers/FieldSales/components/FieldSaleGuard';
import { useSplitIOTreatment, splitioConstants } from 'src/components/SplitIO';
import ServicePlanModal from 'src/containers/SkuPage/Parts/PlanModal';

// Constants and actions
import { SKU_TYPE } from 'src/constants/sku';
import { DIRECT_BOOKING_BASE_LAYOUT } from 'src/constants/common';
import { pageLoaded, layoutUpdate } from 'src/actions/common';
import { bookNow } from 'src/components/BookNowButton/actions';
import { addProductToCart } from 'src/containers/AddSkuPage/actions';
import { userEntersLocationFromAClick } from 'src/selectors/router';
import { planSelector } from 'src/selectors/cart';
import { userSubscriptionSelector } from 'src/selectors/user';
import {
  skuLoaded,
  planAddedOnSkuTrack,
  planRemovedOnSkuTrack,
} from 'src/containers/SkuPage/actions';
import { loadPlanDetails } from 'src/containers/ProductPage/actions';
import PRODUCT_TYPE from 'src/components/Product/ProductHeader/constants';
import { addPlanToCart, removePlanFromCart } from 'src/actions/cart';
import { MONTHLY_PLAN_ID } from 'src/constants/plan';
import { getDirectBookingTechIdOrSlug } from 'src/utils/cookies/directBookingCookie';
import DynamicTabs from 'src/components/Elements/DynamicTabs';
import { PAGE_NAME, TAB_KEYS } from './constants';

// Styles
import styles from './styles.scss';

const components = new Map([
  [TAB_KEYS.FEATURE, ProductFeatures],
  [TAB_KEYS.FAQ, ProductFaq],
  [TAB_KEYS.INSTALLATION, ProductInstallation],
  [TAB_KEYS.COMPATIBILITY, ProductCompatibility],
  [TAB_KEYS.WORKS_WITH, ProductWorksWith],
  [TAB_KEYS.PRODUCT_SPECS, ProductSpecifications],
]);

const ProductPage = ({ seoName, sku: baseSkuData, product, userPlanId }) => {
  const api = useAPI();
  const { error } = useNotice();
  const dispatchReduxAction = useDispatch();
  const fromAClick = useSelector(userEntersLocationFromAClick);
  const serviceHeaderRef = useRef();
  const reviewsRef = useRef(null);
  const [topNavAndHeaderHeight, setTopNavAndHeaderHeight] = useState(0);
  const [variantOption, setVariantOption] = useState(PRODUCT_TYPE.NO_INSTALLATION);
  const [specialistModalVisible, setSpecialistModalVisible] = useState(false);
  const [upsellModalVisible, setUpsellModalVisible] = useState(false);
  const isProductVariant = variantOption === PRODUCT_TYPE.WITH_INSTALLATION;
  const productVariantSku =
    product && product.productVariants.find((p) => p.requiresInstallation === true);
  const seoNameCheck = seoName.includes('with-installation');
  const newSeoName = trimSeoName({ seoName });
  const cartHasPlan = useSelector(planSelector);
  const userHasPlan = useSelector(userSubscriptionSelector);
  const isMember = Boolean(cartHasPlan || userHasPlan);
  const directBookingTechId = getDirectBookingTechIdOrSlug();

  const {
    splitLoaded: planPricingSplitLoaded,
    splitTreatment: planPricingSplitTreatment,
  } = useSplitIOTreatment(splitioConstants.SPLITIONAME_PLAN_PRICING);
  const isPlanPricingTest =
    planPricingSplitLoaded && planPricingSplitTreatment === splitioConstants.ON;

  const fetchMembershipEstimates = async (sku) => {
    if (!sku) return null;
    // If the user already has a plan, or if there's a plan in the cart use that id. Otherwise use the split value
    const { monthlyID: splitPlanId } = getUpsellPlanIds({ isPlanPricingTest });
    const { productVariants = [] } = sku;

    const productWithInstallationSku = productVariants[0];
    const estimatePayload = !userPlanId && { enforce_plan_id:  splitPlanId};
    const [productEstimate, productWithInstallEstimate] = await Promise.all([
      api.skus.estimate({skuId: sku.id },
        ...estimatePayload),
      api.skus.estimate({skuId: productWithInstallationSku.id},
        ...estimatePayload),
    ]);
    const {
      err: prodError,
      data: { prices: prodEstimatePrices },
    } = productEstimate;
    const {
      err: prodInstallError,
      data: { prices: prodwithInstallationPrices },
    } = productWithInstallEstimate;

    if (prodError || prodInstallError) {
      logger(`${PAGE_NAME} - fetchMembershipEstimates`)(prodError, prodInstallError);
      return {};
    }
    const prices = {
      product: prodEstimatePrices,
      productWithInstallation: prodwithInstallationPrices,
    };
    return prices;
  };

  const fetchPlanData = async () => {
    api.toggleLoader(true);
    try {
      const {
        data: { plan },
        err,
      } = await api.plans.subscriptions.show({ id: MONTHLY_PLAN_ID });
      if (err) throw new Error(err);
      dispatchReduxAction(loadPlanDetails({ plan }));
    } catch (e) {
      logger(`${PAGE_NAME} - fetchPlanData`)(e);
    }
    api.toggleLoader(false);
  };

  useEffect(() => {
    if (directBookingTechId) dispatchReduxAction(layoutUpdate(DIRECT_BOOKING_BASE_LAYOUT));
    const setNewSeoName = () => {
      setVariantOption(PRODUCT_TYPE.WITH_INSTALLATION);
      return newSeoName;
    };
    const productSeoName = seoNameCheck ? setNewSeoName() : seoName;

    (async () => {
      let skuData = baseSkuData;
      // Fetch Sku Data
      if (!skuData) {
        try {
          const {
            data: { sku },
            err,
          } = await api.skus.find({ id: productSeoName });
          if (err) {
            error('Product could not be located.');
            return;
          }
          skuData = sku;
        } catch (e) {
          error('Product could not be located.');
          logger(`${PAGE_NAME} - Fetch sku`)(e);
          return;
        }
      }

      // Direct to ServicePage if needed
      if (skuData.type !== SKU_TYPE.PRODUCT) {
        dispatchReduxAction(pageLoaded(PAGE_NAME, { sku: skuData }));
        dispatchReduxAction(replace(skuPath(skuData.seoName)));
        return;
      }

      // Fetch reviews
      let { extra: { reviews = [] } = { reviews: [] } } = skuData;
      if (!reviews.length) {
        try {
          const {
            data: { reviews: responseReviews },
            err,
          } = await api.reviews.sku({ id: skuData.id });
          if (!err) {
            reviews = responseReviews;
          }
        } catch (e) {
          logger(`${PAGE_NAME} - Fetch Reviews`)(e);
        }
      }
      // Fetch Subscription Plans Info
      const plansResult = await getSubscriptionPlansInfo({ api });
      const {
        data: { plans: planProducts },
      } = plansResult;

      const smartHomePlans = formatPlansForReducer({
        planProducts,
        featured: false,
        isPlanPricingTest,
      });

      // Fetch Estimated Member Price
      if (!skuData.subscriptionPrices && skuData.productVariants.length) {
        const pricesInMainFunction = await fetchMembershipEstimates(skuData);
        skuData.subscriptionPrices = pricesInMainFunction;
      }

      dispatchReduxAction(
        pageLoaded(PAGE_NAME, { sku: skuData, reviews, plansInfo: smartHomePlans }),
      );
      dispatchReduxAction(skuLoaded(skuData, fromAClick));
    })();
  }, [seoName, isPlanPricingTest]);

  /*
    Used to customize the location of the sticky tabs and the trigger
    point for the secondary header and bottom footer. See below for notes.
  */
  const handleScrollEvent = () => {
    if (!serviceHeaderRef.current) return null;
    const { bottom } = serviceHeaderRef.current.getBoundingClientRect();
    return setTopNavAndHeaderHeight(bottom);
  };

  useEventListener({
    handler: handleScrollEvent,
    eventName: 'scroll',
  });

  const onChange = (selectedValue) => {
    if (variantOption === selectedValue) return;
    setVariantOption(selectedValue);
    let productSeoName;
    if (selectedValue === PRODUCT_TYPE.NO_INSTALLATION) {
      productSeoName = newSeoName;
    } else {
      productSeoName = seoName.concat('-with-installation');
    }
    dispatchReduxAction(push(productPath(productSeoName)));
  };

  const handleBook = () => {
    if (isProductVariant) {
      const productVariantId = productVariantSku.id;
      dispatchReduxAction(bookNow(productVariantId));
    } else {
      dispatchReduxAction(addProductToCart({ id: product.id }));
    }
  };

  const handleRating = useCallback(() => {
    const { bottom } = serviceHeaderRef.current.getBoundingClientRect();
    window.scrollTo({
      top: reviewsRef.current.offsetTop - bottom,
    });
  }, [product, serviceHeaderRef]);

  if (!product) return <PageLoader />;

  const service = isProductVariant ? productVariantSku : product;
  const serviceSkuSeoName = get(productVariantSku, 'serviceSku.seoName', '');
  let value = null;
  let bullets = [];
  if (product.extra.tabsData.installation) {
    const {
      extra: {
        tabsData: {
          installation: { data },
        },
      },
    } = product;
    value = data.value;
    bullets = data.steps;
  }
  const priceDetails = getInstallationPrice({ product, service: productVariantSku });
  const memberInstallationPrices = getMemberInstallationPrice({
    subscriptionPrices: product.subscriptionPrices,
    service: productVariantSku,
  });

  const onClick = () => {
    const productId = productVariantSku.id;
    dispatchReduxAction(bookNow(productId));
  };

  const handleSpecialistChatClick = () => {
    setSpecialistModalVisible(true);
  };

  const closeSpecialistModal = () => {
    setSpecialistModalVisible(false);
  };

  const handleUpsellToggle = (shouldAdd, planId = MONTHLY_PLAN_ID) => {
    if (shouldAdd) {
      dispatchReduxAction(addPlanToCart(planId));
      dispatchReduxAction(planAddedOnSkuTrack(service, 'New'));
      setUpsellModalVisible(false);
    } else {
      dispatchReduxAction(removePlanFromCart());
      dispatchReduxAction(planRemovedOnSkuTrack(service, 'New'));
    }
  };

  const addPlanFromModal = () => handleUpsellToggle(true, MONTHLY_PLAN_ID);
  const handleUpsellLearnMore = () => {
    fetchPlanData();
    setUpsellModalVisible(true);
  };

  return (
    <div className="paddingTop-small1">
      <ServiceMeta service={product} />
      <Grid.Fluid>
        <Grid.Row>
          <Grid.Column>
            <FieldSaleGuard>
              <Breadcrumbs items={product.extra.breadcrumbs} />
            </FieldSaleGuard>
          </Grid.Column>
        </Grid.Row>
      </Grid.Fluid>

      {/* Sku Gallery and Pricing info */}
      <Grid.Fluid>
        <ProductHeaderBlock
          gallery={product.extra.gallery}
          service={service}
          rating={product.extra.rating.rating}
          ratingCount={product.extra.rating.ratingCount}
          className="paddingBottom-medium2"
          onBook={handleBook}
          onRating={handleRating}
          onChange={onChange}
          bullets={bullets}
          value={value}
          addToCart={onClick}
          onSpecialistChatClick={handleSpecialistChatClick}
          serviceSkuSeoName={serviceSkuSeoName}
          variantOption={variantOption}
          priceDetails={priceDetails}
          memberInstallationPrices={memberInstallationPrices}
          subscriptionPrices={product.subscriptionPrices}
          isProductVariant={isProductVariant}
          isMember={isMember}
          product={product}
          handleUpsellToggle={handleUpsellToggle}
          handleUpsellLearnMore={handleUpsellLearnMore}
          topNavAndHeaderHeight={topNavAndHeaderHeight}
          showInstallationOnlyOption={!directBookingTechId}
        />
      </Grid.Fluid>

      <DynamicTabs
        tabs={product.extra.tabs}
        tabsOuterWrapperClass={styles.tabsOuterWrapper}
        tabsOffsetHeight={topNavAndHeaderHeight}
        subNavRef={serviceHeaderRef}
      >
        {Object.keys(product.extra.tabsData).map((t) =>
          createElement(components.get(t), {
            ...product.extra.tabsData[t],
            className: 'paddingY-large2',
            onClick,
          }),
        )}
      </DynamicTabs>

      {!!product.extra.reviews.length && (
        <CustomerReviews
          slidesToShow={product.extra.reviews.length < 3 ? 2 : 3}
          ref={reviewsRef}
          reviews={product.extra.reviews}
          backgroundColor="grey"
          className={cn('paddingY-large2', styles.reviews)}
          carouselItemStyle={styles.reviewItem}
        />
      )}

      <ProductHero data={product.extra.bottomHero} />
      <ProductSpecialistModal
        visible={specialistModalVisible}
        onCancel={closeSpecialistModal}
        service={service}
      />

      <ServicePlanModal
        plan={product.extra.plan}
        sku={product}
        visible={upsellModalVisible}
        closePlanModal={() => setUpsellModalVisible(false)}
        addPlan={addPlanFromModal}
        plansInfo={product.extra.plansInfo}
      />
    </div>
  );
};

ProductPage.propTypes = {
  seoName: PropTypes.string,
  sku: PropTypes.object,
  product: PropTypes.shape({
    productVariants: PropTypes.array,
    id: PropTypes.number,
    extra: PropTypes.shape({
      bottomHero: PropTypes.object,
      breadcrumbs: PropTypes.arrayOf(PropTypes.object),
      gallery: PropTypes.array,
      reviews: PropTypes.array,
      showStickyBookNow: PropTypes.bool,
      tabs: PropTypes.arrayOf(PropTypes.string),
      tabsData: PropTypes.object,
      rating: PropTypes.shape({
        rating: PropTypes.number,
        ratingCount: PropTypes.number,
      }),
      plan: PropTypes.object,
      plansInfo: PropTypes.object,
    }),
    subscriptionPrices: PropTypes.object,
  }),
  userPlanId: PropTypes.number,
};

export default ProductPage;

/*
  The designs call for the tabs row to stick under the service header
  once the user starts to scroll past the row. To do this, the total
  height of the top nav and serivce header is collected. With this value,
  we can do 2 things:

  1) Customize the trigger point on when to show the service header. An extra 60 pixels is added so the header appears a little earlier.
  2) Set the 'top' value for the sticky tabs row so it will stick under the
  service header.
*/
